// src/components/ui/select.jsx
// shadcn/ui Select on Radix (from the installed radix-ui package), in the
// graphite style (design decision D9). Replaces the earlier native-select
// wrapper in this file, which nothing imported. Native selects in forms keep
// using Select from ./input.jsx.
//
//   <Select value={v} onValueChange={setV}>
//     <SelectTrigger aria-label="Sort by"><SelectValue /></SelectTrigger>
//     <SelectContent>
//       <SelectItem value="auto">Auto</SelectItem>
//     </SelectContent>
//   </Select>
//
// Item values must be non-empty strings (Radix reserves "" for "no value").
import * as React from "react"
import { Select as SelectPrimitive } from "radix-ui"
import { Check, ChevronDown, ChevronUp } from "lucide-react"
import { cn } from "@/lib/utils"

const Select = SelectPrimitive.Root
const SelectGroup = SelectPrimitive.Group
const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-10 w-full items-center justify-between gap-2 rounded-xl bg-[var(--ui-inset)] px-3 text-left text-[13.5px] text-[var(--ui-text)]",
      "transition-colors hover:bg-[var(--ui-inset-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30",
      "disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-[var(--ui-text-muted)] [&>span]:truncate",
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" aria-hidden="true" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = "SelectTrigger"

const SelectContent = React.forwardRef(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      position={position}
      sideOffset={6}
      className={cn(
        "relative z-50 max-h-[min(var(--radix-select-content-available-height),360px)] min-w-[var(--radix-select-trigger-width)] overflow-hidden",
        "rounded-xl bg-[var(--ui-inset)] p-1 text-[var(--ui-text)] shadow-[0_16px_40px_rgba(0,0,0,0.5)] ring-1 ring-white/5",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
        className
      )}
      {...props}
    >
      <SelectPrimitive.ScrollUpButton className="flex h-6 items-center justify-center text-[var(--ui-text-muted)]">
        <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
      </SelectPrimitive.ScrollUpButton>
      <SelectPrimitive.Viewport>{children}</SelectPrimitive.Viewport>
      <SelectPrimitive.ScrollDownButton className="flex h-6 items-center justify-center text-[var(--ui-text-muted)]">
        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
      </SelectPrimitive.ScrollDownButton>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = "SelectContent"

const SelectLabel = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-2.5 pb-1 pt-2 text-[11.5px] text-[var(--ui-text-muted)]", className)}
    {...props}
  />
))
SelectLabel.displayName = "SelectLabel"

const SelectItem = React.forwardRef(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-default select-none items-center rounded-lg py-2 pl-2.5 pr-8 text-[13.5px] outline-none",
      "data-[highlighted]:bg-[var(--ui-chip)] data-[disabled]:pointer-events-none data-[disabled]:opacity-40",
      className
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <span className="absolute right-2.5 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
      </SelectPrimitive.ItemIndicator>
    </span>
  </SelectPrimitive.Item>
))
SelectItem.displayName = "SelectItem"

const SelectSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator ref={ref} className={cn("-mx-1 my-1 h-px bg-[var(--ui-divider)]", className)} {...props} />
))
SelectSeparator.displayName = "SelectSeparator"

export {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue,
}